import type { CSSProperties } from 'react';

/**
 * Typography for operator-authored HTML, scoped to `.lms-rich-text` so
 * it never leaks into block-internal markup. Selectors are
 * `.lms-rich-text > div > …`: the authored HTML always sits one wrapper
 * div deep, which keeps the rules off the wrapper's own children.
 *
 * <p>Exported as a string (not a stylesheet) because the blocks package
 * ships no CSS of its own — every consumer inlines what it needs.
 */
export const RICH_TEXT_CSS = `
  .lms-rich-text > div > h1,
  .lms-rich-text > div > h2,
  .lms-rich-text > div > h3 {
    margin-top: 1.4em;
    margin-bottom: 0.5em;
    font-family: var(--font-family-display, inherit);
    line-height: 1.25;
    color: var(--color-text-primary, #0f1218);
  }
  .lms-rich-text > div > h1 { font-size: 2em; font-weight: 700; }
  .lms-rich-text > div > h2 { font-size: 1.5em; font-weight: 700; }
  .lms-rich-text > div > h3 { font-size: 1.2em; font-weight: 600; }
  .lms-rich-text > div > p { margin: 0 0 1em; }
  .lms-rich-text > div > ul,
  .lms-rich-text > div > ol { margin: 0 0 1em; padding-left: 1.5em; }
  .lms-rich-text > div > li { margin-bottom: 0.4em; }
  .lms-rich-text > div > blockquote {
    margin: 1em 0;
    padding: 0.6em 1em;
    border-left: 3px solid var(--color-brand-500, #3b5bdb);
    color: var(--color-text-secondary, #6a6f73);
    font-style: italic;
  }
  .lms-rich-text > div > pre {
    background: var(--color-neutral-100, #f3f4f6);
    padding: 1em;
    border-radius: 6px;
    overflow-x: auto;
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 0.9em;
  }
  .lms-rich-text > div code {
    background: var(--color-neutral-100, #f3f4f6);
    padding: 2px 5px;
    border-radius: 3px;
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 0.9em;
  }
  .lms-rich-text > div a {
    color: var(--color-brand-600, #1d4ed8);
    text-decoration: underline;
  }
  .lms-rich-text > div img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
    margin: 1em 0;
  }
`;

export interface RichHtmlContentProps {
  /** Operator-authored HTML. Rendered as-is — the admin editor is the trust boundary. */
  html: string;
  /** Merged onto the wrapper, for callers that set their own size/colour. */
  style?: CSSProperties;
}

/**
 * Renders operator-authored HTML with the shared typography above. Use
 * wherever an editor-written body reaches the portal outside a
 * {@code RichText} block — the class alone is not enough, the rules
 * ride along with it.
 */
export function RichHtmlContent({ html, style }: RichHtmlContentProps) {
  return (
    <div className="lms-rich-text" style={style}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
      <style dangerouslySetInnerHTML={{ __html: RICH_TEXT_CSS }} />
    </div>
  );
}
